import React, { useState, useCallback } from 'react'
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Close, CloudUpload, InsertDriveFile } from '@mui/icons-material'
import { useDropzone } from 'react-dropzone'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import { api } from '../../../services/Main/Api'
import { getCondoId } from '../../../services/Main/AuthStorage'

const ImportStatementModal = ({ open, onClose, accountId, onSuccess }) => {
    const { enqueueSnackbar } = useSnackbar()
    const navigate = useNavigate()

    const [file, setFile] = useState(null)
    const [fileFormat, setFileFormat] = useState('ofx')
    const [csvEncoding, setCsvEncoding] = useState('utf-8')
    const [uploading, setUploading] = useState(false)
    const [result, setResult] = useState(null)
    const [error, setError] = useState(null)

    const onDrop = useCallback(
        (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                setFile(acceptedFiles[0])
                setError(null)
            }
        },
        []
    )

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        maxFiles: 1,
        multiple: false,
        maxSize: 10 * 1024 * 1024,
        accept:
            fileFormat === 'ofx'
                ? { 'application/x-ofx': ['.ofx'] }
                : { 'text/csv': ['.csv'] },
        onDropRejected: (rejections) => {
            const err = rejections[0]?.errors?.[0]
            if (err?.code === 'file-too-large') {
                setError('Arquivo muito grande. Tamanho máximo: 10MB')
            } else if (err?.code === 'file-invalid-type') {
                setError(`Formato inválido. Selecione um arquivo .${fileFormat}`)
            } else {
                setError('Arquivo rejeitado')
            }
        },
    })

    const handleUpload = () => {
        if (!file) return
        setUploading(true)
        setError(null)

        const formData = new FormData()
        formData.append('account', accountId)
        formData.append('file', file)
        formData.append('file_format', fileFormat)
        if (fileFormat === 'csv') {
            formData.append('csv_encoding', csvEncoding)
        }

        api.post('cashflow/statement-imports/upload/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
            .then((res) => {
                setResult(res.data)
                enqueueSnackbar('Extrato importado com sucesso!', { variant: 'success' })
                if (onSuccess) onSuccess()
            })
            .catch((err) => {
                const data = err?.response?.data
                let msg = 'Erro ao importar extrato'
                if (data) {
                    if (typeof data === 'string') msg = data
                    else if (data.detail) msg = data.detail
                    else if (data.error) msg = data.error
                    else if (data.error_message) msg = data.error_message
                    else if (data.file) msg = Array.isArray(data.file) ? data.file[0] : data.file
                }
                setError(msg)
                enqueueSnackbar(msg, { variant: 'error' })
            })
            .finally(() => setUploading(false))
    }

    const handleClose = () => {
        setFile(null)
        setResult(null)
        setError(null)
        setFileFormat('ofx')
        setCsvEncoding('utf-8')
        onClose()
    }

    const handleViewEntries = () => {
        handleClose()
        navigate(`/financeiro/contas-bancarias/${accountId}/extrato`)
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Importar Extrato
                </Typography>
                <IconButton size="small" onClick={handleClose}>
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {!result ? (
                    <>
                        <Grid container spacing={2} sx={{ mb: 2.5 }}>
                            <Grid item xs={6}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Formato"
                                    value={fileFormat}
                                    onChange={(e) => {
                                        setFileFormat(e.target.value)
                                        setFile(null)
                                    }}
                                >
                                    <MenuItem value="ofx">OFX</MenuItem>
                                    <MenuItem value="csv">CSV</MenuItem>
                                </TextField>
                            </Grid>
                            {fileFormat === 'csv' && (
                                <Grid item xs={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Codificação"
                                        value={csvEncoding}
                                        onChange={(e) => setCsvEncoding(e.target.value)}
                                    >
                                        <MenuItem value="utf-8">UTF-8</MenuItem>
                                        <MenuItem value="latin-1">Latin-1 (ISO-8859-1)</MenuItem>
                                        <MenuItem value="cp1252">CP1252 (Windows)</MenuItem>
                                    </TextField>
                                </Grid>
                            )}
                        </Grid>

                        <Box
                            {...getRootProps()}
                            sx={{
                                border: '2px dashed',
                                borderColor: isDragActive ? '#3B82F6' : '#E2E8F0',
                                borderRadius: 2,
                                p: 4,
                                textAlign: 'center',
                                bgcolor: isDragActive ? '#EFF6FF' : '#F8FAFC',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': { borderColor: '#3B82F6', bgcolor: '#EFF6FF' },
                            }}
                        >
                            <input {...getInputProps()} />
                            {file ? (
                                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                    <InsertDriveFile sx={{ fontSize: 32, color: '#3B82F6' }} />
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#1E293B', fontWeight: 600 }}
                                        >
                                            {file.name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                            {(file.size / 1024).toFixed(1)} KB
                                        </Typography>
                                    </Box>
                                </Box>
                            ) : (
                                <>
                                    <CloudUpload sx={{ fontSize: 48, color: '#94A3B8', mb: 1 }} />
                                    <Typography
                                        variant="body1"
                                        sx={{ color: '#64748B', fontWeight: 500 }}
                                    >
                                        Arraste o arquivo aqui ou clique para selecionar
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                        Formato aceito: .{fileFormat} (máx. 10MB)
                                    </Typography>
                                </>
                            )}
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}
                    </>
                ) : (
                    <Box>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Importação concluída com sucesso!
                        </Alert>
                        <Box
                            sx={{
                                bgcolor: '#F8FAFC',
                                borderRadius: 2,
                                p: 2.5,
                                border: '1px solid #E2E8F0',
                            }}
                        >
                            <Typography variant="body2" sx={{ color: '#1E293B', mb: 1 }}>
                                <strong>{result.entries_created}</strong> lançamentos criados
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748B' }}>
                                <strong>{result.entries_skipped}</strong> duplicatas ignoradas
                            </Typography>
                        </Box>

                        {result.status === 'error' && result.error_message && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {result.error_message}
                            </Alert>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                {result ? (
                    <>
                        <Button onClick={handleClose} sx={{ textTransform: 'none', color: '#64748B' }}>
                            Fechar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleViewEntries}
                            sx={{
                                bgcolor: '#3B82F6',
                                '&:hover': { bgcolor: '#2563EB' },
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 2,
                            }}
                        >
                            Ver Extrato
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={handleClose} sx={{ textTransform: 'none', color: '#64748B' }}>
                            Cancelar
                        </Button>
                        <LoadingButton
                            variant="contained"
                            loading={uploading}
                            disabled={!file}
                            onClick={handleUpload}
                            sx={{
                                bgcolor: '#3B82F6',
                                '&:hover': { bgcolor: '#2563EB' },
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 2,
                            }}
                        >
                            Importar
                        </LoadingButton>
                    </>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default ImportStatementModal
